import { Dispatch } from "redux";
import * as Web3Lib from "web3";
import { AbiItem } from "web3-utils";
import nftABI from "../contractData/abis/nft.json";
import propertyFactoryABI from "../contractData/abis/propertyFactory.json";
import contractAddresses from "../contractData/contractAddress/addresses";
import { services } from "../services";

const Web3 = (Web3Lib as any).default || Web3Lib;
type Web3Instance = InstanceType<typeof Web3>;

// Define types for actions
interface ActionType<T = unknown> {
  type: string;
  data?: T;
}

interface Web3Data {
  accounts: string[];
  web3?: Web3Instance;
  error?: string | boolean;
  code?: number;
  msg?: string;
  isLoggedIn?: boolean;
}

interface NetworkIdResponse {
  networkId: string | number;
}

interface UserBalances {
  [key: string]: string | number;
}

// Define contract method types
interface ContractMethod {
  call: () => Promise<unknown>;
  send: (options: {
    from: string;
    value?: string | number;
  }) => Promise<unknown>;
}

interface ContractMethods {
  [methodName: string]: (...args: unknown[]) => ContractMethod;
}

interface ContractInstance {
  methods: ContractMethods;
  options: {
    address: string;
  };
  events: {
    [eventName: string]: unknown;
  };
}

// Custom implementation for getUserBalances as it doesn't exist in services
function getUserBalances() {
  return (dispatch: Dispatch<ActionType<UserBalances>>) => {
    // Create a mock/empty balance response
    const mockBalances: UserBalances = {
      ETH: "0",
      // Add any other tokens as needed
    };

    // Dispatch the mock balances
    dispatch(fetchUserBalances(mockBalances));

    // Alternatively, if you want to use Web3 directly:
    // Note: This is commented out because it would require special handling
    // of the Web3 instance which isn't fully typed in your services
    /*
    services.getWeb3().then((web3Data) => {
      if (web3Data && web3Data.accounts && web3Data.accounts.length > 0) {
        // Here you would use Web3 to get balances
        // This is a placeholder - you'll need to implement the actual balance fetching
        const balances: UserBalances = {
          ETH: "0" // placeholder
        };
        dispatch(fetchUserBalances(balances));
      }
    });
    */
  };
}

function fetchUserBalances(data: UserBalances): ActionType<UserBalances> {
  return {
    type: "FETCH_USER_BALANCES", // dispatch user login event
    data: data,
  };
}

function setDispatchData<T>(data: T, type: string): ActionType<T> {
  return { data: data, type: type };
}

// get network id
function getNetworkId() {
  return (dispatch: Dispatch<ActionType<NetworkIdResponse>>) => {
    const response = services.getNetworkId();
    response.then((promise) => {
      if (promise) {
        // Fix for the network ID type issue - cast the promise to NetworkIdResponse
        const networkIdResponse: NetworkIdResponse =
          promise as unknown as NetworkIdResponse;
        dispatch(setDispatchData(networkIdResponse, "FETCH_NETWORK_ID"));
      }
    });
  };
}

function getWeb3(val?: boolean) {
  if (val) {
    return (dispatch: Dispatch<ActionType<null>>) => {
      dispatch(setDispatchData(null, "FETCH_WEB3_DATA"));
    };
  } else {
    return (dispatch: Dispatch<ActionType<Web3Data>>) => {
      const response = services.getWeb3();
      response.then((promise?: Web3Data) => {
        // Check if user is using thirdweb
        const usingThirdweb = localStorage.getItem("usingThirdweb");
        const userAddress = localStorage.getItem("userAddress");
        const rwaAuthToken = localStorage.getItem("RwaAuthToken");

        if (promise?.accounts?.[0]) {
          // If we have accounts from web3, use them
          dispatch(setDispatchData(promise, "FETCH_WEB3_DATA"));
        } else if (usingThirdweb && userAddress && rwaAuthToken) {
          // For thirdweb users, if we have userAddress and token but no accounts from web3,
          // create a web3Data object with the stored address
          const web3Data: Web3Data = {
            isLoggedIn: true,
            accounts: [userAddress],
          };
          dispatch(setDispatchData(web3Data, "FETCH_WEB3_DATA"));
        } else if (!usingThirdweb) {
          // Only clear localStorage for non-thirdweb users when no accounts are found
          localStorage.clear();
        }
        // For thirdweb users without accounts, don't clear localStorage
        // This prevents the userAddress from being cleared during authentication
      });
    };
  }
}

function enabledWalletConnect() {
  return (dispatch: Dispatch<ActionType<Web3Data>>) => {
    const response = services.enabledWalletConnect();
    response.then((promise?: Web3Data) => {
      if (promise) {
        dispatch(setDispatchData(promise, "FETCH_WEB3_DATA"));
      } else {
        // console.log('error in actions');
      }
    });
  };
}

function enableMetamask() {
  return (dispatch: Dispatch<ActionType<Web3Data>>) => {
    const response = services.enableMetamask();
    response.then((promise?: Web3Data) => {
      if (promise && !promise.error) {
        dispatch(setDispatchData(promise, "FETCH_WEB3_DATA"));
      } else if (promise) {
        dispatch(setDispatchData(promise, "FETCH_WEB3_DATA_ERROR"));
      }
    });
  };
}

function getNFTContractInstance() {
  const { nftContractAddress } = contractAddresses();
  return (dispatch: Dispatch<ActionType<ContractInstance>>) => {
    // Cast the ABI to AbiItem[] to match the expected type
    const typedNftABI = nftABI as AbiItem[];

    const response = services.getContractInstance(
      typedNftABI,
      nftContractAddress
    );
    response.then((promise?: ContractInstance) => {
      // console.log(promise);
      if (promise) {
        dispatch(setDispatchData(promise, "NFT_CONTRACT_INSTANCE"));
      } else {
        // console.log('error in actions');
      }
    });
  };
}

function getPropertyContractInstance() {
  const addresses = contractAddresses();
  const propertyFactoryAddress = addresses.propertyFactory;

  return (dispatch: Dispatch<ActionType<ContractInstance>>) => {
    // Cast the ABI to AbiItem[] to match the expected type
    const typedPropertyFactoryABI = propertyFactoryABI as AbiItem[];

    const response = services.getContractInstance(
      typedPropertyFactoryABI,
      propertyFactoryAddress
    );
    response.then((promise?: ContractInstance) => {
      // console.log(promise);
      if (promise) {
        dispatch(setDispatchData(promise, "PROPERTY_CONTRACT_INSTANCE"));
      } else {
        // console.log('error in actions');
      }
    });
  };
}

function createPropertyContractInstance(contractAddress: string) {
  return (dispatch: Dispatch<ActionType<ContractInstance>>) => {
    // Cast the ABI to AbiItem[] to match the expected type
    const typedPropertyFactoryABI = propertyFactoryABI as AbiItem[];

    const response = services.getContractInstance(
      typedPropertyFactoryABI,
      contractAddress
    );
    response.then((promise?: ContractInstance) => {
      if (promise) {
        dispatch(setDispatchData(promise, "PROPERTY_CONTRACT_INSTANCE"));
      } else {
        // console.log('error in actions');
      }
    });
  };
}

export const web3Actions = {
  getNetworkId,
  getWeb3,
  enableMetamask,
  getUserBalances,
  getNFTContractInstance,
  enabledWalletConnect,
  createPropertyContractInstance,
  getPropertyContractInstance,
};
